import {
  zefActionPayload,
  zefActionFailPayload,
  zefActionSuccessPayload
} from '@zerops/zef/core';
import {
  AutoscalingProfileOverridesValue,
  ServiceStack,
  ServiceStackAutoscaling,
  ServiceStackBackupConfig,
  ServiceStackBackupResponse,
  ProjectBackupListResponse,
  ServiceStackPort,
  ServiceStackUpdateData,
  TriggerPipelinePayload
} from '@zerops/models/service-stack';
import { Process } from '@zerops/models/process';
import { createAction, union } from '@ngrx/store';
import { PipelineDebugModes, PipelineDebugPhases, ServiceStackOperationTypes } from './service-stack-base.constant';
import type { ZagentAgentType } from '@zerops/zerops/core/zerops-services';

// -- add
export const addServiceStack = createAction(
  '[@zerops/zerops/service-stack-base] add-service',
  // meta: { quickadd: true } in case of quick add
  zefActionPayload<{
    projectId: string;
    serviceVersion: string;
    serviceData: any
  }>()
);

export const addServiceStackFail = createAction(
  '[@zerops/zerops/service-stack-base] add-service/fail',
  zefActionFailPayload
);

export const addServiceStackSuccess = createAction(
  '[@zerops/zerops/service-stack-base] add-service/success',
  zefActionSuccessPayload<ServiceStack & { process: Process; }>()
);

// -- enable subdomain access
export const enableSubdomainAccess = createAction(
  '[@zerops/zerops/service-stack-base] enable-subdomain-access',
  zefActionPayload<{ id: string; }>()
);

export const enableSubdomainAccessFail = createAction(
  '[@zerops/zerops/service-stack-base] enable-subdomain-access/fail',
  zefActionFailPayload
);

export const enableSubdomainAccessSuccess = createAction(
  '[@zerops/zerops/service-stack-base] enable-subdomain-access/success',
  zefActionSuccessPayload<Process>()
);

// -- disable subdomain access
export const disableSubdomainAccess = createAction(
  '[@zerops/zerops/service-stack-base] disable-subdomain-access',
  zefActionPayload<{ id: string; }>()
);

export const disableSubdomainAccessFail = createAction(
  '[@zerops/zerops/service-stack-base] disable-subdomain-access/fail',
  zefActionFailPayload
);

export const disableSubdomainAccessSuccess = createAction(
  '[@zerops/zerops/service-stack-base] disable-subdomain-access/success',
  zefActionSuccessPayload<Process>()
);

// -- get a container file, like the Nginx config, through the file browser api
export const getContainerFile = createAction(
  '[@zerops/zerops/service-stack-base] get-container-file',
  zefActionPayload<{ serviceStackId: string; url: string; containerId: string; filePath: string }>()
);

export const getContainerFileFail = createAction(
  '[@zerops/zerops/service-stack-base] get-container-file/fail',
  zefActionFailPayload
);

export const getContainerFileSuccess = createAction(
  '[@zerops/zerops/service-stack-base] get-container-file/success',
  zefActionSuccessPayload<string>()
);

// -- start
export const serviceStackStart = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-start',
  zefActionPayload<{
    id: string;
    type: ServiceStackOperationTypes;
  }>()
);

export const serviceStackStartFail = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-start/fail',
  zefActionFailPayload
);

export const serviceStackStartSuccess = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-start/success',
  zefActionSuccessPayload<Process>()
);

// -- stop
export const serviceStackStop = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-stop',
  zefActionPayload<{
    id: string;
    type: ServiceStackOperationTypes;
  }>()
);

export const serviceStackStopFail = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-stop/fail',
  zefActionFailPayload
);

export const serviceStackStopSuccess = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-stop/success',
  zefActionSuccessPayload<Process>()
);

// -- restart
export const serviceStackRestart = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-restart',
  zefActionPayload<{
    id: string;
    type: ServiceStackOperationTypes;
  }>()
);

export const serviceStackRestartFail = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-restart/fail',
  zefActionFailPayload
);

export const serviceStackRestartSuccess = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-restart/success',
  zefActionSuccessPayload<Process>()
);

// -- reload
export const serviceStackReload = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-reload',
  zefActionPayload<{
    id: string;
    type: ServiceStackOperationTypes;
  }>()
);

export const serviceStackReloadFail = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-reload/fail',
  zefActionFailPayload
);

export const serviceStackReloadSuccess = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-reload/success',
  zefActionSuccessPayload<Process>()
);

//  -- modify internal ports
export const serviceStackModifyInternalPorts = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-modify-internal-ports',
  zefActionPayload<{ serviceStackId: string; ports: ServiceStackPort[]; id?: string }>()
);

export const serviceStackModifyInternalPortsFail = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-modify-internal-ports/fail',
  zefActionFailPayload
);

export const serviceStackModifyInternalPortsSuccess = createAction(
  '[@zerops/zerops/service-stack-base] service-stack-modify-internal-ports/success',
  zefActionSuccessPayload<Process>()
);

// -- external repository integration
export const integrateExternalRepository = createAction(
  '[@zerops/zerops/service-stack-base] integrate-external-repository',
  zefActionPayload<{
    id: string;
    gitLabIntegration?: any;
    gitHubIntegration?: any;
  }>()
);

export const integrateExternalRepositoryFail = createAction(
  '[@zerops/zerops/service-stack-base] integrate-external-repository/fail',
  zefActionFailPayload
);

export const integrateExternalRepositorySuccess = createAction(
  '[@zerops/zerops/service-stack-base] integrate-external-repository/success',
  zefActionSuccessPayload()
);

export const removeExternalRepositoryIntegration = createAction(
  '[@zerops/zerops/service-stack-base] remove-external-repository-integration',
  zefActionPayload<{
    id: string
  }>()
);

// -- remove external repository integration
export const removeExternalRepositoryIntegrationFail = createAction(
  '[@zerops/zerops/service-stack-base] remove-external-repository-integration/fail',
  zefActionFailPayload
);

export const removeExternalRepositoryIntegrationSuccess = createAction(
  '[@zerops/zerops/service-stack-base] remove-external-repository-integration/success',
  zefActionSuccessPayload()
);

//  -- update object storage settings
export const updateObjectStorageSettings = createAction(
  '[@zerops/zerops/service-stack-base] update-object-storage-settings',
  zefActionPayload<{ id: string; diskGBytes: number; policy?: string; rawPolicy?: string; cdnEnabled?: boolean; }>()
);

export const updateObjectStorageSettingsFail = createAction(
  '[@zerops/zerops/service-stack-base] update-object-storage-settings/fail',
  zefActionFailPayload
);

export const updateObjectStorageSettingsSuccess = createAction(
  '[@zerops/zerops/service-stack-base] update-object-storage-settings/success',
  zefActionSuccessPayload<Process>()
);

// -- stack update
export const updateServiceStack = createAction(
  '[@zerops/zerops/service-stack-base] update-service-stack',
  zefActionPayload<{ id: string; typeVersion: string; data: ServiceStackUpdateData }>()
);

export const updateServiceStackFail = createAction(
  '[@zerops/zerops/service-stack-base] update-service-stack/fail',
  zefActionFailPayload
);

export const updateServiceStackSuccess = createAction(
  '[@zerops/zerops/service-stack-base] update-service-stack/success',
  zefActionSuccessPayload<Process>()
);

// `canRevealSecrets` is resolved by the caller (which knows the viewer's project role)
// and forwarded to the export dialog, so the dialog itself needs no role awareness -
// it must not import from core, that would close a cycle back through this effect.
export const serviceStackExport = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-export',
  zefActionPayload<{ id: string; reveal?: boolean; canRevealSecrets?: boolean; }>()
);

export const serviceStackExportFail = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-export/fail',
  zefActionFailPayload
);

export const serviceStackExportSuccess = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-export/success',
  zefActionSuccessPayload<string>()
);

export const serviceStackImport = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-import',
  zefActionPayload<{ yaml: string; projectId: string; recipeSource?: string; recipeSourceUrl?: string; }>()
);

export const serviceStackImportFail = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-import/fail',
  zefActionFailPayload
);

export const serviceStackImportSuccess = createAction(
  '[@zerops/zerops/core/service-stack-base] service-stack-import/success',
  zefActionSuccessPayload<{ projectId: string; projectName: string; serviceStacks: ServiceStack[]; }>()
);

// -- first-class recipe import (development containers)
//
// `zcpAgents` (optional): the list of agents the user picked at form-submit
// time for a ZCP deploy. Carried alongside the YAML so the auth-dialog
// effect can seed its walker queue without round-tripping through a userData
// index — the per-agent `ZCP_AGENT_*_<SUFFIX>` envs are sufficient for every
// other consumer, but the deploy-time walker needs the list BEFORE userData
// has finished loading on the new stack. Non-ZCP deploys leave it unset.
export const firstClassRecipeImport = createAction(
  '[@zerops/zerops/core/service-stack-base] first-class-recipe-import',
  zefActionPayload<{
    projectId: string;
    serviceImportYaml: string;
    recipeSource?: string;
    recipeSourceUrl?: string;
    createIntegrationToken?: boolean;
    zcpAgents?: ZagentAgentType[];
  }>()
);

export const firstClassRecipeImportFail = createAction(
  '[@zerops/zerops/core/service-stack-base] first-class-recipe-import/fail',
  zefActionFailPayload
);

export const firstClassRecipeImportSuccess = createAction(
  '[@zerops/zerops/core/service-stack-base] first-class-recipe-import/success',
  zefActionSuccessPayload<{ projectId: string; projectName: string; processes: Process[]; }>()
);

// -- adjust autoscaling
export const adjustServiceStackAutoscaling = createAction(
  '[@zerops/zerops/service-stack-base] adjust-service-stack-autoscaling',
  zefActionPayload<{
    serviceStackId: string;
    autoscalingProfileId?: string;
    customAutoscaling: ServiceStackAutoscaling;
    autoscalingProfileOverrides?: AutoscalingProfileOverridesValue | null;
  }>()
);

export const adjustServiceStackAutoscalingFail = createAction(
  '[@zerops/zerops/service-stack-base] adjust-service-stack-autoscaling/fail',
  zefActionFailPayload
);

export const adjustServiceStackAutoscalingSuccess = createAction(
  '[@zerops/zerops/service-stack-base] adjust-service-stack-autoscaling/success',
  zefActionSuccessPayload<{ process: Process }>()
);

// -- clear build cache
export const deleteBuildCache = createAction(
  '[@zerops/zerops/service-stack-base] delete-build-cache',
  zefActionPayload<{ id: string; }>()
);

export const deleteBuildCacheFail = createAction(
  '[@zerops/zerops/service-stack-base] delete-build-cache/fail',
  zefActionFailPayload
);

export const deleteBuildCacheSuccess = createAction(
  '[@zerops/zerops/service-stack-base] delete-build-cache/success',
  zefActionSuccessPayload()
);

// -- clear runtime prepare cache
export const deleteRuntimePrepareCache = createAction(
  '[@zerops/zerops/service-stack-base] delete-runtime-prepare-cache',
  zefActionPayload<{ id: string; }>()
);

export const deleteRuntimePrepareCacheFail = createAction(
  '[@zerops/zerops/service-stack-base] delete-runtime-prepare-cache/fail',
  zefActionFailPayload
);

export const deleteRuntimePrepareCacheSuccess = createAction(
  '[@zerops/zerops/service-stack-base] delete-runtime-prepare-cache/success',
  zefActionSuccessPayload()
);

// -- get list of backups
export const getBackups = createAction(
  '[@zerops/zerops/service-stack-base] get-backups',
  zefActionPayload<{ id: string; }>()
);

export const getBackupsFail = createAction(
  '[@zerops/zerops/service-stack-base] get-backups/fail',
  zefActionFailPayload
);

export const getBackupsSuccess = createAction(
  '[@zerops/zerops/service-stack-base] get-backups/success',
  zefActionSuccessPayload<ServiceStackBackupResponse>()
);

// -- get project backups
export const getProjectBackups = createAction(
  '[@zerops/zerops/service-stack-base] get-project-backups',
  zefActionPayload<{ projectId: string; sort?: string[]; offset?: number; }>()
);

export const getProjectBackupsFail = createAction(
  '[@zerops/zerops/service-stack-base] get-project-backups/fail',
  zefActionFailPayload
);

export const getProjectBackupsSuccess = createAction(
  '[@zerops/zerops/service-stack-base] get-project-backups/success',
  zefActionSuccessPayload<ProjectBackupListResponse>()
);

// -- create backup
export const createBackup = createAction(
  '[@zerops/zerops/service-stack-base] create-backup',
  zefActionPayload<{ id: string; tags?: string[] }>()
);

export const createBackupFail = createAction(
  '[@zerops/zerops/service-stack-base] create-backup/fail',
  zefActionFailPayload
);

export const createBackupSuccess = createAction(
  '[@zerops/zerops/service-stack-base] create-backup/success',
  zefActionSuccessPayload<{ success: boolean; async: boolean; }>()
);

// -- download backup
export const backupDownloadUrl = createAction(
  '[@zerops/zerops/service-stack-base] backup-download-url',
  zefActionPayload<{
    id: string;
    projectId: string;
    serviceId: string;
    filename: string;
  }>()
);

export const backupDownloadUrlFail = createAction(
  '[@zerops/zerops/service-stack-base] backup-download-url/fail',
  zefActionFailPayload
);

export const backupDownloadUrlSuccess = createAction(
  '[@zerops/zerops/service-stack-base] backup-download-url/success',
  zefActionSuccessPayload<{
    filename: string,
    id: string,
    projectId: string,
    serviceId: string
  }>()
);

// -- get backup download link
export const copyBackupDownloadLink = createAction(
  '[@zerops/zerops/service-stack-base] copy-backup-download-link',
  zefActionPayload<{ projectId: string; serviceId: string; name: string; }>()
);

export const copyBackupDownloadLinkFail = createAction(
  '[@zerops/zerops/service-stack-base] copy-backup-download-link/fail',
  zefActionFailPayload
);

export const copyBackupDownloadLinkSuccess = createAction(
  '[@zerops/zerops/service-stack-base] copy-backup-download-link/success',
  zefActionSuccessPayload<{ projectId: string; serviceId: string; name: string; }>()
);

// -- set backup config
export const setBackupConfig = createAction(
  '[@zerops/zerops/service-stack-base] set-backup-config',
  zefActionPayload<{
    id?: string;
    config: ServiceStackBackupConfig;
    serviceId: string;
  }>()
);

export const setBackupConfigFail = createAction(
  '[@zerops/zerops/service-stack-base] set-backup-config/fail',
  zefActionFailPayload
);

export const setBackupConfigSuccess = createAction(
  '[@zerops/zerops/service-stack-base] set-backup-config/success',
  zefActionSuccessPayload()
);

// -- delete backup
export const deleteBackup = createAction(
  '[@zerops/zerops/service-stack-base] delete-backup',
  zefActionPayload<{
    id: string;
    projectId: string;
    serviceId: string;
  }>()
);

export const deleteBackupFail = createAction(
  '[@zerops/zerops/service-stack-base] delete-backup/fail',
  zefActionFailPayload
);

export const deleteBackupSuccess = createAction(
  '[@zerops/zerops/service-stack-base] delete-backup/success',
  zefActionSuccessPayload<{ projectId: string; serviceId: string; id: string; }>()
);

// -- pipeline debug
export const pipelineDebug = createAction(
  '[@zerops/zerops/service-stack-base] build-debug',
  zefActionPayload<{ id: string, phase: PipelineDebugPhases, mode: PipelineDebugModes }>()
);

export const pipelineDebugFail = createAction(
  '[@zerops/zerops/service-stack-base] build-debug/fail',
  zefActionFailPayload
);

export const pipelineDebugSuccess = createAction(
  '[@zerops/zerops/service-stack-base] build-debug/success',
  zefActionSuccessPayload<Record<PipelineDebugPhases, PipelineDebugModes>>()
);

// -- trigger redeploy
export const triggerRedeploy = createAction(
  '[@zerops/zerops/service-stack-base] trigger-redeploy',
  zefActionPayload<{ id: string; envVarsFile?: string; envSecretsFile?: string; }>()
);

export const triggerRedeployFail = createAction(
  '[@zerops/zerops/service-stack-base] trigger-redeploy/fail',
  zefActionFailPayload
);

export const triggerRedeploySuccess = createAction(
  '[@zerops/zerops/service-stack-base] trigger-redeploy/success',
  zefActionSuccessPayload<{ process: Process; }>()
);

// -- trigger pipeline
export const triggerPipeline = createAction(
  '[@zerops/zerops/service-stack-base] trigger-pipeline',
  zefActionPayload<{ id: string; data: TriggerPipelinePayload; }>()
);

export const triggerPipelineFail = createAction(
  '[@zerops/zerops/service-stack-base] trigger-pipeline/fail',
  zefActionFailPayload
);

export const triggerPipelineSuccess = createAction(
  '[@zerops/zerops/service-stack-base] trigger-pipeline/success',
  zefActionSuccessPayload<{ process: Process; }>()
);

const all = union({
  addServiceStack,
  addServiceStackFail,
  addServiceStackSuccess,
  enableSubdomainAccess,
  enableSubdomainAccessFail,
  enableSubdomainAccessSuccess,
  disableSubdomainAccess,
  disableSubdomainAccessFail,
  disableSubdomainAccessSuccess,
  serviceStackStart,
  serviceStackStartFail,
  serviceStackStartSuccess,
  serviceStackStop,
  serviceStackStopFail,
  serviceStackStopSuccess,
  serviceStackRestart,
  serviceStackRestartFail,
  serviceStackRestartSuccess,
  serviceStackReload,
  serviceStackReloadFail,
  serviceStackReloadSuccess,
  getContainerFile,
  getContainerFileFail,
  getContainerFileSuccess,
  serviceStackModifyInternalPorts,
  serviceStackModifyInternalPortsSuccess,
  serviceStackModifyInternalPortsFail,
  integrateExternalRepository,
  integrateExternalRepositoryFail,
  integrateExternalRepositorySuccess,
  removeExternalRepositoryIntegration,
  removeExternalRepositoryIntegrationFail,
  removeExternalRepositoryIntegrationSuccess,
  serviceStackExport,
  serviceStackExportSuccess,
  serviceStackExportFail,
  serviceStackImport,
  serviceStackImportSuccess,
  serviceStackImportFail,
  firstClassRecipeImport,
  firstClassRecipeImportFail,
  firstClassRecipeImportSuccess,
  deleteBuildCache,
  deleteBuildCacheSuccess,
  deleteBuildCacheFail,
  deleteRuntimePrepareCache,
  deleteRuntimePrepareCacheSuccess,
  deleteRuntimePrepareCacheFail,
  getBackups,
  getBackupsFail,
  getBackupsSuccess,
  getProjectBackups,
  getProjectBackupsFail,
  getProjectBackupsSuccess,
  createBackup,
  createBackupFail,
  createBackupSuccess,
  backupDownloadUrl,
  backupDownloadUrlFail,
  backupDownloadUrlSuccess,
  setBackupConfig,
  setBackupConfigFail,
  setBackupConfirmSuccess: setBackupConfigSuccess,
  deleteBackup,
  deleteBackupFail,
  deleteBackupSuccess,
  pipelineDebug,
  pipelineDebugFail,
  pipelineDebugSuccess,
  triggerPipeline,
  triggerPipelineFail,
  triggerPipelineSuccess,
  copyBackupDownloadLink,
  copyBackupDownloadLinkFail,
  copyBackupDownloadLinkSuccess
});
export type ServiceStackBaseActionUnion = typeof all;
